import { z } from "zod";

import { paginationMetaSchema } from "./base-api-response";
import { createPaginatedResponseSchema } from "./pagination";

/**
 * Discount type enum
 */
export enum DiscountType {
  PRODUCT = "PRODUCT",
  CART = "CART",
  BOGO = "BOGO",
  SHIPPING = "SHIPPING",
}

/**
 * Discount value type enum
 */
export enum DiscountValueType {
  PERCENTAGE = "PERCENTAGE",
  FIXED_AMOUNT = "FIXED_AMOUNT",
}

/**
 * Discount status enum
 */
export enum DiscountStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
}

/**
 * Discount target type enum
 */
export enum DiscountTargetType {
  ALL = "ALL",
  SPECIFIC_PRODUCTS = "SPECIFIC_PRODUCTS",
  SPECIFIC_CATEGORIES = "SPECIFIC_CATEGORIES",
}

/**
 * Discount visibility enum
 */
export enum DiscountVisibility {
  APP = "APP",
  WEB = "WEB",
}

/**
 * Discount information in an order response
 */
export const discountResponseSchemaForOrder = z.object({
  id: z.string(),
  code: z.string(),
  amount: z.number(),
  valueType: z.nativeEnum(DiscountValueType),
  value: z.number(),
});

export type OrderDiscountResponseDto = z.infer<typeof discountResponseSchemaForOrder>;

/**
 * Discount schema - entity + sploot-platform v4 GET payloads (nullable fields and `stackPriority` match API).
 */
export const discountSchema = z.object({
  id: z.string(),
  code: z.string()
    .trim()
    .min(3, "Code must be at least 3 characters")
    .max(50, "Code cannot exceed 50 characters")
    .toUpperCase(),
  name: z.string()
    .trim()
    .min(3, "Name must be at least 3 characters")
    .max(100, "Name cannot exceed 100 characters"),
  type: z.nativeEnum(DiscountType),
  valueType: z.nativeEnum(DiscountValueType),
  value: z.number().min(0, "Value must be a positive number"),
  status: z.nativeEnum(DiscountStatus),
  description: z.string().max(1000, "Description cannot exceed 1000 characters").nullish(),
  minimumPurchaseAmount: z.number().min(0, "Minimum purchase amount must be a positive number").nullish(),
  maximumDiscountAmount: z.number().min(0, "Maximum discount amount must be a positive number"),
  targetType: z.nativeEnum(DiscountTargetType),
  applicableProductIds: z.array(z.string()).optional(),
  applicableCategoryIds: z.array(z.string()).optional(),
  maxUsesTotal: z.number().int().min(0, "Usage limit must be a positive integer").default(10),
  maxUsesPerUser: z.number().int().min(0, "Per user limit must be a positive integer").default(10),
  usageCount: z.number().optional(),
  startDate: z.number().int().min(0, "Start date must be a valid timestamp"),
  endDate: z.number().int().min(0, "End date must be a valid timestamp").optional(),
  isCombinableWithOther: z.boolean().default(true),
  stackPriority: z.number().int().min(0, "Stacking priority must be a positive integer").optional(),
  eligibleUserSegments: z.array(z.string()).optional(),
  eligibleUserRoles: z.array(z.string()).optional(),
  eligibleSubscriptionTypes: z.array(z.string()).optional(),
  eligibleGeographies: z.array(z.string()).optional(),
  visibility: z.array(z.nativeEnum(DiscountVisibility)).optional(),
  userIds: z.array(z.string()).optional(),
  createdAt: z.number(),
  createdBy: z.string().optional(),
  updatedAt: z.number(),
  updatedBy: z.string().optional(),
  isDeleted: z.boolean().optional().default(false),
})
.refine(
  data => {
    // If target type is specific products, productIds must be provided
    if (
      data.targetType === DiscountTargetType.SPECIFIC_PRODUCTS &&
      (!data.applicableProductIds || data.applicableProductIds.length === 0)
    ) {
      return false;
    }
    // If target type is specific categories, categoryIds must be provided
    if (
      data.targetType === DiscountTargetType.SPECIFIC_CATEGORIES &&
      (!data.applicableCategoryIds || data.applicableCategoryIds.length === 0)
    ) {
      return false;
    }
    return true;
  },
  {
    message: "Product or category IDs must be provided based on target type",
    path: ["targetType"],
  },
)
.refine(
  data => {
    // Ensure endDate is after startDate if both are provided
    if (data.endDate && data.startDate && data.endDate <= data.startDate) {
      return false;
    }
    return true;
  },
  {
    message: "End date must be after start date",
    path: ["endDate"],
  },
)
.refine(
  data => {
    // Percentage discount value should be between 0 and 100
    if (
      data.valueType === DiscountValueType.PERCENTAGE &&
      (data.value < 0 || data.value > 100)
    ) {
      return false;
    }
    return true;
  },
  {
    message: "Percentage discount value must be between 0 and 100",
    path: ["value"],
  },
);

export type IDiscount = z.infer<typeof discountSchema>;

/** v4 discount list — `fetchDiscounts` maps `meta` to the table shape. */
export const discountListDataSchema = createPaginatedResponseSchema(discountSchema);

/**
 * Discount form data interface for form handling
 */
export interface DiscountFormData {
  code: string;
  name: string;
  type: DiscountType;
  valueType: DiscountValueType;
  value: number;
  status: DiscountStatus;
  description: string;
  minimumPurchaseAmount: number;
  maximumDiscountAmount: number;
  targetType: DiscountTargetType;
  maxUsesTotal: number;
  maxUsesPerUser: number;
  startDate: string; // ISO date string for form inputs
  endDate?: string; // ISO date string for form inputs
  isCombinableWithOther: boolean;
  stackPriority?: number;
  visibility?: DiscountVisibility[];
  userIds?: string[]; // New field for user selection
}

/**
 * Form validation schema specifically for DiscountFormData
 */
export const discountFormSchema = z.object({
  code: z.string().min(1, "Code is required").max(50, "Code must be less than 50 characters"),
  name: z.string().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  type: z.nativeEnum(DiscountType),
  valueType: z.nativeEnum(DiscountValueType),
  value: z.number().min(0, "Value must be positive"),
  status: z.nativeEnum(DiscountStatus),
  description: z.string().min(1, "Description is required").max(1000, "Description must be less than 1000 characters"),
  minimumPurchaseAmount: z.number().min(0, "Minimum purchase amount must be positive"),
  maximumDiscountAmount: z.number().min(0, "Maximum discount amount must be positive"),
  targetType: z.nativeEnum(DiscountTargetType),
  maxUsesTotal: z.number().min(1, "Max uses total must be at least 1").default(10),
  maxUsesPerUser: z.number().min(1, "Max uses per user must be at least 1").default(10),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().optional(),
  isCombinableWithOther: z.boolean(),
  stackPriority: z.number().min(0).optional(),
  visibility: z.array(z.nativeEnum(DiscountVisibility)).optional(),
  userIds: z.array(z.string()).optional(),
}).refine((data) => {
  if (data.valueType === DiscountValueType.PERCENTAGE && data.value > 100) {
    return false;
  }
  return true;
}, {
  message: "Percentage discounts cannot exceed 100%",
  path: ["value"]
}).refine((data) => {
  if (data.endDate && data.startDate) {
    return new Date(data.endDate) > new Date(data.startDate);
  }
  return true;
}, {
  message: "End date must be after start date",
  path: ["endDate"]
});

// Export the inferred type from the schema
export type DiscountFormSchemaType = z.infer<typeof discountFormSchema>;

/**
 * Create discount request DTO to match backend
 */
export interface CreateDiscountRequestDto {
  code: string;
  name: string;
  type: DiscountType;
  valueType: DiscountValueType;
  value: number;
  status: DiscountStatus;
  description: string;
  minimumPurchaseAmount: number;
  maximumDiscountAmount: number;
  targetType: DiscountTargetType;
  applicableProductIds?: string[];
  applicableCategoryIds?: string[];
  maxUsesTotal: number;
  maxUsesPerUser: number;
  startDate: number; // timestamp
  endDate?: number; // timestamp
  isCombinableWithOther: boolean;
  stackPriority?: number;
  eligibleUserSegments?: string[];
  eligibleUserRoles?: string[];
  eligibleSubscriptionTypes?: string[];
  eligibleGeographies?: string[];
  visibility?: DiscountVisibility[];
  userIds?: string[];
}

/**
 * Update discount request DTO to match backend
 */
export interface UpdateDiscountRequestDto {
  code?: string;
  name?: string;
  type?: DiscountType;
  valueType?: DiscountValueType;
  value?: number;
  status?: DiscountStatus;
  description?: string | null;
  minimumPurchaseAmount?: number | null;
  maximumDiscountAmount?: number | null;
  targetType?: DiscountTargetType;
  applicableProductIds?: string[] | null;
  applicableCategoryIds?: string[] | null;
  maxUsesTotal?: number | null;
  maxUsesPerUser?: number | null;
  startDate?: number; // timestamp
  endDate?: number | null; // timestamp
  isCombinableWithOther?: boolean;
  stackPriority?: number | null;
  eligibleUserSegments?: string[] | null;
  eligibleUserRoles?: string[] | null;
  eligibleSubscriptionTypes?: string[] | null;
  eligibleGeographies?: string[] | null;
  visibility?: DiscountVisibility[] | null;
  userIds?: string[] | null;
}

/**
 * Discount disabled fields interface for field management
 */
export interface DiscountDisabledFields {
  code: boolean;
  name: boolean;
  type: boolean;
  valueType: boolean;
  value: boolean;
  status: boolean;
  description: boolean;
  minimumPurchaseAmount: boolean;
  maximumDiscountAmount: boolean;
  targetType: boolean;
  maxUsesTotal: boolean;
  maxUsesPerUser: boolean;
  startDate: boolean;
  endDate: boolean;
  isCombinableWithOther: boolean;
  stackPriority: boolean;
  visibility: boolean;
  userIds: boolean;
}

/**
 * Discount list response schema — meta shape matches paginationMetaSchema from the platform API.
 */
export const discountListResponseSchema = z.object({
  items: z.array(discountSchema),
  meta: paginationMetaSchema,
});

export type IDiscountListResponse = z.infer<typeof discountListResponseSchema>;

/**
 * Discount sort fields
 */
export type DiscountSortField = 
  | "code" 
  | "name" 
  | "type" 
  | "status" 
  | "value" 
  | "usageCount" 
  | "startDate" 
  | "endDate" 
  | "createdAt";

/**
 * Discount table column definition
 */
export type IDiscountColumn = {
  id: keyof IDiscount | "actions";
  label: string;
  sortable?: boolean;
  searchable?: boolean;
  filterable?: boolean;
  filterOptions?: { label: string; value: string }[];
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "center" | "right";
  format?: (value: unknown, row?: IDiscount) => string | React.ReactNode;
};
